import * as React from "react";
import { matchPath, useLocation } from "react-router-dom";

import { breakpoints } from "@/providers/theme/themes";
import { Goto } from "@/shared/navigation/Goto";

import { NavigationSidebarContext } from "./SidebarContext";
import { AppSidebarStorage } from "./sidebarStorage";

const mediumSizedScreenQuery = `(max-width: ${breakpoints.md}px)`;
const xLargeSizedScreenQuery = `(max-width: ${breakpoints.xl}px)`;

/**
 * Routes where the drawer is preferred over the sidebar to save space.
 * For these routes, the sidebar will collapse to a drawer at XL breakpoint
 * instead of the default MD breakpoint.
 */
const DrawerPreferredRoutes = [Goto.route.Visualize];

export function NavigationSidebarProvider({
  children,
}: React.PropsWithChildren) {
  const location = useLocation();

  // Determine if current route prefers drawer over sidebar
  const isDrawerPreferred = DrawerPreferredRoutes.some((route) =>
    matchPath({ path: `${route}/*`, end: false }, location.pathname),
  );

  // Use XL breakpoint for drawer-preferred routes, MD for others
  const screenQuery = isDrawerPreferred
    ? xLargeSizedScreenQuery
    : mediumSizedScreenQuery;

  const [isSidebarOpen, setIsSidebarOpen] = React.useState(() => {
    return AppSidebarStorage.getOpen();
  });

  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  const open = React.useCallback(() => {
    const isSmallScreen = window.matchMedia(screenQuery).matches;
    if (isSmallScreen) {
      setIsDrawerOpen(true);
    } else {
      setIsSidebarOpen(true);
      AppSidebarStorage.setOpen(true);
    }
  }, [screenQuery]);

  const close = React.useCallback(() => {
    const isSmallScreen = window.matchMedia(screenQuery).matches;
    if (isSmallScreen) {
      setIsDrawerOpen(false);
    } else {
      setIsSidebarOpen(false);
      AppSidebarStorage.setOpen(false);
    }
  }, [screenQuery]);

  /** React to changes in screen size */
  React.useLayoutEffect(
    function onSmallScreenSize() {
      const mediaQuery = window.matchMedia(screenQuery);

      // Set initial state based on current screen size
      if (mediaQuery.matches) {
        // Screen is small - force sidebar closed (use drawer instead)
        setIsSidebarOpen(false);
      } else {
        // Screen is large enough - restore user's preference
        const userPreference = AppSidebarStorage.getOpen();
        setIsSidebarOpen(userPreference);
      }

      const handleMediaQueryChange = (event: MediaQueryListEvent) => {
        if (event.matches) {
          // Screen became small - force sidebar closed
          setIsSidebarOpen(false);
        } else {
          // Screen became large - restore user's preference
          const userPreference = AppSidebarStorage.getOpen();
          setIsSidebarOpen(userPreference);
        }
      };
      mediaQuery.addEventListener("change", handleMediaQueryChange);
      return () => {
        mediaQuery.removeEventListener("change", handleMediaQueryChange);
      };
    },
    [screenQuery],
  );

  /** Close drawer on navigation (including browser back/forward) */
  React.useEffect(
    function closeDrawerOnNavigation() {
      setIsDrawerOpen(false);
    },
    [location.pathname],
  );

  return (
    <NavigationSidebarContext.Provider
      value={{
        isDrawerOpen,
        isSidebarOpen,
        close,
        open,
      }}
    >
      {children}
    </NavigationSidebarContext.Provider>
  );
}
